import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import axios, { CancelToken } from "axios";
import moment from "moment";

import { StatusCode } from "api/protocols";

import { showError, showSuccess } from "redux/actions/ui";
import { searchHistoryUpdateSearch } from "redux/actions/searchBars/searchHistory";
import { ICustomer } from "redux/reducers/models/customer";
import { ITournament, ITournamentParticipant } from "redux/reducers/models/tournament";

import { useAppDispatch } from "hooks/redux";
import useModal from "hooks/modals/useModal";

import Page from "components/page/Page";
import Sheet from "components/sheet/Sheet";
import Tabs, { GenericTab } from "components/tabs/Tabs";
import FormLayout from "components/form/FormLayout";
import Search from "components/search/Search";
import Callout from "components/callout/Callout";
import Spin from "components/spin/spin";
import Input from "components/form/input/Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CopyTournamentParticipants,
  DeleteTournamentParticipant,
  GetTournamentParticipant,
  ImportTournamentPlayers,
  IPostTournamentParticipantNew,
  PutExportParticipants,
  PostParticipant,
  PutParticipant,
} from "api/rpc/2024-04/facilityAdmin/tournament/participant";
import Card from "components/card/Card";

import { Badge } from "components/badge/Badge";
import { downloadCsv, valueToString } from "helpers/Helpers";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Icon from "components/icon/Icon";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { Select } from "components/select/index";
import { GetCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import {
  GetTournamentRegistrationInputResponse,
  GetTournamentRegistrationInputResponseExport,
  TTournamentRegistrationInputResponse,
} from "api/rpc/2024-04/facilityAdmin/tournament/registration/inputs";
import Checkbox from "components/form/checkbox/Checkbox";
import "./TournamentPlayers.scss";

interface ITournamentState {
  tournament: ITournament;
}

interface IImportState {
  modalVisible: boolean;
  selectedFile: File;
}

interface ICopyPlayerState {
  showCopyPlayers: boolean;
  selectedTournament: any;
  tournamentSearch: string;
  tournaments: Array<ITournament>;
  tournamentSearching: boolean;
}

interface IPlayerAnswersModal {
  answers: TTournamentRegistrationInputResponse[];
  participantId: number;
  loadingStatus: "finished" | "loading" | "failed";
}

export default function TournamentPlayers() {
  const { t } = useTranslation();
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const fileUploadRef = useRef(null);
  const { Option } = Select;

  const dispatch = useAppDispatch();
  const [tournaments] = useState<Array<ITournament>>([]);

  const [tournamentPlayers, setTournamentPlayers] = useState<ITournamentParticipant[]>([]);

  const [searchState, setSearchState] = useState({
    value: "",
    customers: [] as ICustomer[],
  });

  const [copyPlayerState, setCopyPlayerState] = useState<ICopyPlayerState>({
    showCopyPlayers: false,
    selectedTournament: null,
    tournamentSearch: "",
    tournaments: [],
    tournamentSearching: false,
  });

  const [state, setState] = useState<ITournamentState>({
    tournament: undefined,
  });

  const [importState, setImportState] = useState<IImportState>({
    modalVisible: false,
    selectedFile: null,
  });

  const {
    state: newPlayerModal,
    updateModal,
    closeModal,
  } = useModal<IPostTournamentParticipantNew & { tabIndex: number }>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    tournament_id: tournamentId,
    tabIndex: 0,
  });

  const {
    state: playerAnswersModal,
    closeModal: closePlayerAnswersModal,
    updateModal: updatePlayerAnswersModal,
  } = useModal<IPlayerAnswersModal>({
    answers: [],
    participantId: null,
    loadingStatus: "finished",
    clearOnClose: true,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTournamentPlayers(source.token);
    void loadTournament();
    return () => source.cancel();
  }, [tournamentId]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (searchState.value.length === 0) {
      return;
    }
    void searchCustomers(source.token);
    return () => source.cancel();
  }, [searchState.value]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (playerAnswersModal.loadingStatus === "loading") {
      void loadTournamentRegistrationInputResponse(source.token);
    }

    return () => source.cancel();
  }, [playerAnswersModal.loadingStatus]);

  async function loadTournamentPlayers(token?: CancelToken) {
    if (tournamentPlayers !== undefined) {
      setTournamentPlayers(undefined);
    }

    const tournamentPlayersRes = await GetTournamentParticipant(
      { tournament_id: tournamentId },
      token ? false : true,
      token,
    );

    if (token?.reason) {
      return;
    }
    if (tournamentPlayersRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tournament participants.")));
    }

    setTournamentPlayers(tournamentPlayersRes.status !== StatusCode.OK ? [] : tournamentPlayersRes.data);
  }

  async function searchCustomers(token?: CancelToken) {
    if (searchState.customers !== undefined) {
      setSearchState(prev => ({ ...prev, customers: undefined }));
    }
    const customerRes = await GetCustomer({ search: searchState.value }, token ? false : true, token);

    console.log("Customers;", customerRes);
    if (token.reason) {
      return;
    }
    setSearchState(prev => ({ ...prev, customers: customerRes.status !== StatusCode.OK ? [] : customerRes.data }));
  }

  // Add previously created user to the tournament
  async function addNewPlayer(id: string | number) {
    const addPlayerRes = await PostParticipant({ tournament_id: tournamentId, customer_id: id }, true);

    if (addPlayerRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding new player to tournament.")));
      return;
    }

    void loadTournamentPlayers();
    closeModal();
  }

  // Create user & add them to the Tournament
  async function createPlayer() {
    const { ...playerParams } = newPlayerModal;

    const params = {
      ...playerParams,
      tournament_id: tournamentId,
    };

    const createPlayerRes = await PostParticipant(params, true);
    console.log(createPlayerRes);

    if (createPlayerRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating player for tournament.")));
      return;
    }

    void loadTournamentPlayers();
    closeModal();
  }

  function tabChange(index: number) {
    // Reset inputs when tabbed away
    if (index !== 0) {
      setSearchState(prev => ({ ...prev, value: "", customers: [] }));
      dispatch(searchHistoryUpdateSearch("tournament_participants", ""));
    }

    if (index !== 1) {
      updateModal({ tabIndex: index, first_name: "", last_name: "", email: "", phone: "" });
      return;
    }

    updateModal({ tabIndex: index });
  }

  const primaryAction = {
    content: t("secure.facility.tournament.tournament_players.001"),
    action: () => updateModal({ isOpen: true }),
  };

  async function deleteTournamentParticipant(id: number) {
    const deleteParticipantRes = await DeleteTournamentParticipant({ participant_id: id }, true);

    if (deleteParticipantRes.status === StatusCode.OK) {
      dispatch(showSuccess(t("Successfully deleted the participant.")));
      void loadTournamentPlayers(); // refresh the table
    } else {
      dispatch(showError(t("Something went wrong deleting the tournament participant.")));
    }
  }

  async function loadTournament() {
    const numericTournamentId = Number(tournamentId);
    const tournamentRes = await GetTournament({ id: numericTournamentId }, false);

    if (tournamentRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting tournament")));
      return;
    }

    const tournament = tournamentRes.data[0];

    setState(prevState => ({
      ...prevState,
      tournament,
    }));
  }

  async function updatePlayerFinancialStatus(event: React.MouseEvent<HTMLElement, MouseEvent>, player: any) {
    event.stopPropagation();

    const wantedStatus = player.order_financial_status == "paid" || null ? "unpaid" : "paid";

    const params = {
      id: player.id,
      order_financial_status: wantedStatus,
    };

    // Update player within the League
    const updatePlayerInTournamentRes = await PutParticipant(params, true);
    console.log(updatePlayerInTournamentRes);
    if (updatePlayerInTournamentRes.status !== StatusCode.OK) {
      return;
    }

    void loadTournamentPlayers();
    void loadTournament();
  }

  function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files;
    if (file && file.length > 0) {
      setImportState(prevState => ({ ...prevState, selectedFile: file[0] }));
    }
  }

  async function importTournamentPlayers() {
    const formData = new FormData();

    formData.append("file", importState.selectedFile);
    formData.append("tournament_id", tournamentId);

    const importRes = await ImportTournamentPlayers(formData, true);

    if (importRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error importing tournament players")));
    }

    fileUploadRef.current.value = "";
    setImportState(prevState => ({ ...prevState, selectedFile: null, modalVisible: false }));
    const source = axios.CancelToken.source();
    void loadTournamentPlayers(source.token);
  }

  async function copyPlayer() {
    const params = {
      to_tournament_id: copyPlayerState.selectedTournament.id,
      from_tournament_id: tournamentId,
    };

    const copyRes = await CopyTournamentParticipants(params, true);

    if (copyRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error copying players")));
      return;
    }

    dispatch(showSuccess(t("Successfully copied players")));

    setCopyPlayerState(prevState => ({ ...prevState, showCopyPlayers: false, selectedTournament: null }));
  }

  function displayTournaments() {
    if (
      copyPlayerState?.tournaments &&
      copyPlayerState?.tournaments?.length > 0 &&
      copyPlayerState?.tournamentSearch !== ""
    ) {
      return copyPlayerState?.tournaments;
    } else {
      return tournaments;
    }
  }

  async function loadSearchedTournaments(search: string) {
    const params = {
      search: search,
    };

    const tournamentRes = await GetTournament(params, false);

    if (tournamentRes.status !== StatusCode.OK) {
      dispatch(showError(tournamentRes.message));

      return [];
    }

    return tournamentRes.data;
  }

  async function exportTournamentPlayers() {
    const exportRes = await PutExportParticipants({ tournament_id: Number(tournamentId), type: "csv" }, true);

    if (exportRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.tournament.tournament_players.018")));
      return;
    }

    const participantExport = document.createElement("a");
    participantExport.href = "data:text/csv;charset=utf-8," + encodeURI(exportRes.data as string);
    participantExport.target = "_blank";
    participantExport.download = `${state.tournament.name}_participants_${moment(new Date())
      .format("YYYY-MM-DD")
      .toString()}`
      .replace(/ /g, "_")
      .toLowerCase();
    participantExport.click();
  }

  function handleTournamentChange(value: any, id: string, tournament: ITournament) {
    setCopyPlayerState(prevState => ({ ...prevState, tournamentSearch: "", selectedTournament: tournament }));
  }

  function removeSelectedTournament() {
    setCopyPlayerState(prevState => ({
      ...prevState,
      selectedTournament: null,
      tournaments: [],
      tournamentSearching: false,
    }));
  }

  //Load Leagues
  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    const search = () => {
      timeoutId = global.setTimeout(() => {
        void (async () => {
          if (copyPlayerState.tournamentSearch !== "") {
            try {
              setCopyPlayerState(prevState => ({ ...prevState, tournamentSearching: true }));
              const tournaments = await loadSearchedTournaments(copyPlayerState.tournamentSearch);
              if (mounted) {
                setCopyPlayerState(prevState => ({
                  ...prevState,
                  tournaments: tournaments,
                  tournamentSearching: false,
                }));
              }
            } catch (error) {
              console.log("err", error);
            }
            return;
          } else {
            setCopyPlayerState(prevState => ({ ...prevState, tournaments: [], tournamentSearching: false }));
          }
        })();
      }, 1000);
    };
    search();
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [copyPlayerState.tournamentSearch]);

  async function loadTournamentRegistrationInputResponse(cancelToken: CancelToken) {
    const playerResponsesRes = await GetTournamentRegistrationInputResponse(
      { participant_id: playerAnswersModal.participantId },
      false,
      cancelToken,
    );

    if (cancelToken?.reason) {
      updatePlayerAnswersModal({
        loadingStatus: "finished",
        answers: [],
      });

      return;
    } else if (playerResponsesRes.status !== StatusCode.OK) {
      updatePlayerAnswersModal({
        loadingStatus: "failed",
      });

      return;
    }

    updatePlayerAnswersModal({
      loadingStatus: "finished",
      answers: playerResponsesRes.data,
    });
  }

  function handleViewAnswersClick(participantId: number) {
    updatePlayerAnswersModal({
      isOpen: true,
      participantId,
      loadingStatus: "loading",
    });
  }

  function renderPlayerAnswersModalContent() {
    if (playerAnswersModal.loadingStatus === "loading") {
      return (
        <div className="tournament-players-player-form-spinner-container">
          <Spin className="tournament-players-player-form-spinner" />
        </div>
      );
    } else if (playerAnswersModal.loadingStatus === "failed") {
      return <Callout type="error" title={t("Failed to load participant answers")} />;
    } else if (playerAnswersModal.loadingStatus === "finished") {
      if (playerAnswersModal.answers.length === 0) {
        return <Callout type="info" title={t("There are no answers")} />;
      } else {
        return (
          <div className="tournament-players-player-form-responses">
            {playerAnswersModal.answers.map((answer, index) => {
              return (
                <React.Fragment key={index}>
                  {answer.type === "checkbox" && (
                    <Checkbox readOnly size="small" checked={answer.value as boolean} label={answer.label} />
                  )}
                  {answer.type === "input" && <Input readOnly value={answer.value as string} label={answer.label} />}
                  {answer.type === "select" && (
                    <Select defaultValue={answer.value as string} label={answer.label}>
                      <Option value={answer.value as string}>{answer.value}</Option>
                    </Select>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        );
      }
    }
  }

  async function exportTournamentResponses() {
    const exportRes = await GetTournamentRegistrationInputResponseExport({ tournament_id: Number(tournamentId) }, true);

    if (exportRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting tournament responses")));
      return;
    }

    const templateExport = document.createElement("a");

    templateExport.href = "data:text/csv;charset=utf-8," + encodeURI(exportRes.data);
    templateExport.target = "_blank";
    templateExport.download = `${"Player_Responses"}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase()
      .concat(".csv");
    templateExport.click();
  }

  function downloadPlayerTemplate() {
    const headers = [
      'customer_first_name',
      'customer_last_name',
      'customer_email',
      'customer_phone',
      'financial_status',
      'notes',
    ];
    downloadCsv(headers.join(','), 'tournment_players.csv');
  }

  return (
    <Page
      title={t("secure.facility.tournament.tournament_players.009")}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("secure.facility.tournament.tournament_players.001"),
              handler: () => updateModal({ isOpen: true }),
            },
            {
              type: "handler",
              label: t("Import"),
              handler: () => setImportState(prevState => ({ ...prevState, modalVisible: true })),
            },
            {
              type: "handler",
              label: t("Export"),
              handler: exportTournamentPlayers,
            },
            {
              type: "handler",
              label: t("Download Template"),
              handler: downloadPlayerTemplate,
            },
            {
              icon: "arrow-down-to-line",
              type: "handler",
              label: t("Download Responses"),
              handler: exportTournamentResponses,
            },
            {
              type: "handler",
              label: t("Copy Players"),
              handler: () => setCopyPlayerState(prevState => ({ ...prevState, showCopyPlayers: true })),
            },
          ],
        },
      }}
    >
      <Card>
        <Card.Section>
          <div className="flex">
            <div className="player-count-container">
              <p className="player-counts">{"Total Players"}</p>
              <p className="player-counts">{tournamentPlayers?.length}</p>
            </div>

            <div className="player-count-container">
              <p className="player-counts">{t("secure.facility.league.players.019")} </p>
              <p className="player-counts">{state.tournament?.total_paid_participant_count} </p>
            </div>
          </div>
        </Card.Section>

        <Card.Section>
          <table className="ui-table league-players">
            <thead>
              <tr>
                <th>{t("secure.facility.tournament.tournament_players.002")}</th>
                <th>{t("Email")}</th>
                {/* <th>{ t("secure.facility.tournament.tournament_players.003")}</th> */}

                <th>{t("secure.facility.tournament.tournament_players.004")}</th>
                <th>{"Player Type"}</th>
                <th>{t("Status")}</th>
                <th>{t("secure.facility.tournament.tournament_players.006")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tournamentPlayers?.map(player => {
                return (
                  <tr key={player.id}>
                    <td>{player.customer.full_name}</td>
                    <td>{player.customer.email}</td>
                    <td>{player.customer.phone}</td>
                    <td>{player.customer.customer_type}</td>

                    <td>
                      {" "}
                      {player.order_financial_status ? (
                        <>
                          {player.order_financial_status === "paid" ? (
                            <span style={{ cursor: "pointer" }}>
                              <Badge
                                type="success"
                                size="medium"
                                iconLeft
                                icon="check"
                                onClick={e => updatePlayerFinancialStatus(e, player)}
                              >
                                {valueToString(player.order_financial_status, "capitalize")}
                              </Badge>
                            </span>
                          ) : (
                            <span style={{ cursor: "pointer" }}>
                              <Badge
                                type="gray"
                                size="medium"
                                iconLeft
                                icon="circle"
                                onClick={e => updatePlayerFinancialStatus(e, player)}
                              >
                                {valueToString(player.order_financial_status, "capitalize")}
                              </Badge>
                            </span>
                          )}
                        </>
                      ) : (
                        <span style={{ cursor: "pointer" }}>
                          <Badge
                            type="gray"
                            size="medium"
                            iconLeft
                            icon="circle"
                            onClick={e => updatePlayerFinancialStatus(e, player)}
                          >
                            {t("secure.facility.league.players.020")}
                          </Badge>
                        </span>
                      )}
                    </td>
                    <td>{moment(player.created_at).format("LL")}</td>
                    <td>
                      <NavigationDropdownNew
                        showPlainTextLabel
                        rightAlign
                        label={<FontAwesomeIcon icon={["far", "chevron-down"]} />}
                        sections={[
                          [
                            {
                              icon: "ban",
                              label: t("Remove"),
                              onClick: () => deleteTournamentParticipant(player.id),
                            },
                            {
                              label: t("View answers"),
                              icon: "eye",
                              onClick: () => handleViewAnswersClick(player.id),
                              disabled: false,
                            },
                          ],
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card.Section>
      </Card>

      <Sheet
        open={playerAnswersModal.isOpen}
        size="medium"
        closable
        stacked
        title={t("Player Form Answers")}
        onCancel={closePlayerAnswersModal}
      >
        {renderPlayerAnswersModalContent()}
      </Sheet>

      <Sheet
        open={newPlayerModal.isOpen}
        size="small"
        closable
        stacked
        title={t("secure.facility.tournament.tournament_players.010")}
        onCancel={() => {
          closeModal();
          setSearchState({ value: "", customers: [] });
          dispatch(searchHistoryUpdateSearch("tournament_participants", ""));
        }}
        onOk={createPlayer}
        cancelText={t("secure.facility.tournament.tournament_players.011")}
        okText={t("secure.facility.tournament.tournament_players.012")}
        okDisabled={
          newPlayerModal.first_name.length === 0 ||
          newPlayerModal.last_name.length === 0 ||
          (newPlayerModal.email.length === 0 && newPlayerModal.phone.length === 0)
        }
      >
        <Tabs tabs={tournamentTabs(t)} selected={newPlayerModal.tabIndex} onSelect={tabChange}>
          {newPlayerModal.tabIndex === 0 ? (
            <div style={{ marginTop: "20px" }}>
              <div className="flex-grow mb-4">
                <Search
                  historyKey={"tournament_participants"}
                  searchCallback={searchValue =>
                    setSearchState(prev => ({
                      ...prev,
                      value: searchValue,
                      customers: searchValue.length === 0 ? [] : prev.customers,
                    }))
                  }
                  placeholder={t("secure.facility.tournament.tournament_players.013")}
                />
              </div>

              <div className="flex-grow mb-4 min-h-[95px]">
                {/* Loading State || Empty State || Customers display */}
                {searchState.customers === undefined ? (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Spin className="inline-flex" />
                  </div>
                ) : searchState.customers.length === 0 ? (
                  <Callout
                    type="info"
                    title={t("Player List")}
                    content={t("Players who match with the search text above will be displayed here.")}
                  />
                ) : (
                  searchState.customers
                    ?.filter(
                      value => !tournamentPlayers?.map(participant => participant.customer.id).includes(value.id),
                    )
                    ?.map((customer, index: number) => {
                      return (
                        <div
                          key={index}
                          className="flex justify-between league-customer-results"
                          onClick={() => addNewPlayer(customer.id)}
                        >
                          <span>
                            <p className="font-semibold text-lg">{customer?.full_name}</p>
                            <p className="text-sm text-gray-500">{customer.email}</p>
                            <p className="text-sm text-gray-500">{customer.phone ? customer.phone : null}</p>
                          </span>
                        </div>
                      );
                    })
                )}
              </div>
            </div>
          ) : null}

          {newPlayerModal.tabIndex === 1 ? (
            <FormLayout>
              <FormLayout.Group>
                <Input
                  placeholder={t("secure.facility.tournament.tournament_players.014")}
                  id="first_name"
                  value={newPlayerModal.first_name}
                  onChange={e => updateModal({ first_name: e.target.value })}
                />
                <Input
                  placeholder={t("secure.facility.tournament.tournament_players.015")}
                  id="last_name"
                  value={newPlayerModal.last_name}
                  onChange={e => updateModal({ last_name: e.target.value })}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Input
                  placeholder={t("secure.facility.tournament.tournament_players.016")}
                  id="email"
                  value={newPlayerModal.email}
                  onChange={e => updateModal({ email: e.target.value })}
                />
                <Input
                  placeholder={t("secure.facility.tournament.tournament_players.017")}
                  id="phone"
                  value={newPlayerModal.phone}
                  onChange={e => updateModal({ phone: e.target.value })}
                />
              </FormLayout.Group>
            </FormLayout>
          ) : null}
        </Tabs>
      </Sheet>

      <Sheet
        title={t("Select File")}
        open={importState.modalVisible}
        size="small"
        closable
        onCancel={() => setImportState(prevState => ({ ...prevState, modalVisible: false }))}
        onOk={importTournamentPlayers}
        okText={t("Import")}
        okDisabled={!importState.selectedFile ? true : false}
      >
        <Callout
          type="info"
          title={!importState.selectedFile ? "No file selected" : "Selected File"}
          content={!importState.selectedFile ? t("Select a file to continue") : importState.selectedFile.name}
        />

        <div className="customer-import-container mt-2">
          <input
            ref={fileUploadRef}
            className="customer-import-input"
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
          />
          <FontAwesomeIcon className="customer-import-icon" icon={["far", "upload"]} />
        </div>
      </Sheet>

      <Sheet
        open={copyPlayerState.showCopyPlayers}
        size="small"
        closable
        stacked
        title={t("Select Tournament")}
        onCancel={() =>
          setCopyPlayerState(prevState => ({ ...prevState, showCopyPlayers: false, selectedTournament: null }))
        }
        onOk={copyPlayer}
        cancelText={t("Cancel")}
        okText={t("Copy")}
        okDisabled={false}
      >
        <div style={{ height: "250px" }}>
          {copyPlayerState.selectedTournament ? (
            <div>
              <div>
                <p className="event-label">{t('Tournaments')}</p>
              </div>

              <div className="selected-container">
                <div className="event-name">
                  <div>{copyPlayerState.selectedTournament.name}</div>

                  <div>
                    <button className="" onClick={removeSelectedTournament}>
                      <Icon icon={faTimes} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <Select
                showSearch
                label={t("Tournaments")}
                onSearch={(query: string) =>
                  setCopyPlayerState(prevState => ({ ...prevState, tournamentSearch: query }))
                }
                onChange={(value: any, tournament: ITournament) =>
                  handleTournamentChange(value, "selectedTournament", tournament)
                }
                placeholder={t("Search tournaments...")}
                allowClear
                searchValue={copyPlayerState.tournamentSearch}
                showDropDownOnFocus={true}
                searching={copyPlayerState.tournamentSearching}
              >
                {displayTournaments()?.map((tournament: any, index: number) => {
                  return (
                    <Option value={tournament?.id} key={index} extraValues={tournament}>
                      <div className="text-semibold text-lg">{tournament.name}</div>
                    </Option>
                  );
                })}
              </Select>
            </div>
          )}
        </div>
      </Sheet>
    </Page>
  );
}

const tournamentTabs = (t: TFunction): GenericTab[] => {
  return [
    {
      id: "search-player",
      content: t("secure.facility.tournament.tournament_players.007"),
    },
    {
      id: "add-player",
      content: t("secure.facility.tournament.tournament_players.008"),
    },
  ];
};
